/** Zona 5 (tela L-02): navegação por capítulo e por página.
 *
 * O capítulo ATIVO não vem de Intersection Observer, como manda o critério da
 * US-02: em vista paginada não há rolagem contínua para observar — a página
 * inteira troca de uma vez. `sectionForPage` resolve pelo número da página, que
 * é exato em vez de aproximado. O critério vale para o HTML5, que rola.
 */
import { PageInput } from '@/features/reader/components/PageInput'
import { useReaderStore } from '@/features/reader/stores/readerStore'
import { adjacentSections, isSectionReleased } from '@/features/reader/utils/toc'
import type { BookAccess } from '@/types/api'

interface BottomBarProps {
  access: BookAccess
}

export function BottomBar({ access }: BottomBarProps) {
  const page = useReaderStore((s) => s.page)
  const irParaPagina = useReaderStore((s) => s.irParaPagina)
  const total = access.page_count ?? 0
  const { anterior, proxima } = adjacentSections(access.toc, page)

  return (
    <footer className="reader-bottombar">
      <button
        type="button"
        onClick={() => anterior && irParaPagina(anterior.page_start, total)}
        disabled={!anterior || !isSectionReleased(access, anterior)}
        aria-label={anterior ? `Capítulo anterior: ${anterior.title}` : 'Capítulo anterior'}
        title={anterior ? `Capítulo anterior: ${anterior.title}` : 'Capítulo anterior'}
      >
        <span aria-hidden="true">«</span>
      </button>

      <nav className="reader-page-controls" aria-label="Navegação de páginas do livro">
        <button type="button" aria-label="Página anterior" title="Página anterior (←)"
          aria-keyshortcuts="ArrowLeft" disabled={page <= 1}
          onClick={() => irParaPagina(page - 1, total)}><span aria-hidden="true">‹</span></button>
        <PageInput
        page={page}
        total={total}
        onIr={(destino) => irParaPagina(destino, total)}
      />
        <button type="button" aria-label="Próxima página" title="Próxima página (→)"
          aria-keyshortcuts="ArrowRight" disabled={total <= 0 || page >= total}
          onClick={() => irParaPagina(page + 1, total)}><span aria-hidden="true">›</span></button>
      </nav>

      <button
        type="button"
        onClick={() => proxima && irParaPagina(proxima.page_start, total)}
        disabled={!proxima || !isSectionReleased(access, proxima)}
        aria-label={proxima ? `Próximo capítulo: ${proxima.title}` : 'Próximo capítulo'}
        title={proxima ? `Próximo capítulo: ${proxima.title}` : 'Próximo capítulo'}
      >
        <span aria-hidden="true">»</span>
      </button>
    </footer>
  )
}
